/**
 * Performance metrics collection.
 *
 * What gets measured
 * ------------------
 * - boot:       bundle-entry start() → first user-facing screen mounted
 * - firstRender: bundle-entry start() → first React commit
 * - videoStart: <video> src set → 'playing' event fires
 * - mediaCache: cache hit rate over a 5-minute sliding window
 *
 * Why client-side instead of server-side
 * --------------------------------------
 * Boot and video start are user-perceived latencies; we want them
 * attributed to the specific board + bundle version. Server-side
 * telemetry can't see them.
 *
 * Where they go
 * -------------
 * Every metric is shipped via `logger.info` with a structured
 * `metric` key in the context. Downstream the API can split them
 * out into a dedicated dashboards/timeseries store; for now the
 * existing logs pipeline is enough to surface regressions ("video
 * start went from 1.2s to 8s after release 0.1.5" stands out in the
 * logs viewer).
 *
 * Threshold-based warnings:
 * - video start > VIDEO_START_WARN_MS → warn
 * - video start > VIDEO_START_ERROR_MS → error (also Sentry via logger sink)
 */

import { logger } from './logger';

const VIDEO_START_WARN_MS = 3_000;
const VIDEO_START_ERROR_MS = 10_000;

// ── Boot ────────────────────────────────────────────────────────────

let bootStartMs: number | null = null;
let firstRenderMs: number | null = null;
let bootCompleteReported = false;

export function markBootStart(): void {
  bootStartMs = performance.now();
  firstRenderMs = null;
  bootCompleteReported = false;
}

export function markFirstRender(): void {
  if (firstRenderMs != null) return;
  if (bootStartMs == null) return;
  firstRenderMs = performance.now() - bootStartMs;
  logger.info('[perf] first render', {
    metric: 'firstRender',
    ms: Math.round(firstRenderMs),
  });
}

/**
 * Boot has reached a stable user-facing screen. Reports the total
 * wall-clock time from `markBootStart()`. Idempotent.
 */
export function markBootComplete(): void {
  if (bootCompleteReported) return;
  if (bootStartMs == null) return;
  bootCompleteReported = true;
  const ms = Math.round(performance.now() - bootStartMs);
  logger.info('[perf] boot complete', {
    metric: 'boot',
    ms,
    firstRenderMs: firstRenderMs != null ? Math.round(firstRenderMs) : null,
  });
}

// ── Video start ─────────────────────────────────────────────────────

interface VideoTiming {
  startMs: number;
  src: string;
  reported: boolean;
}

const videoTimings = new WeakMap<HTMLVideoElement, VideoTiming>();

/**
 * Instrument a <video> element so we measure the delay between
 * "src assigned (or play() requested)" and the 'playing' event.
 *
 * Call this once per video lifecycle. Safe to call multiple times on
 * the same element — only the first reading counts.
 */
export function trackVideoStart(video: HTMLVideoElement, src: string): void {
  if (videoTimings.has(video)) return;
  const timing: VideoTiming = {
    startMs: performance.now(),
    src,
    reported: false,
  };
  videoTimings.set(video, timing);

  const onPlaying = () => {
    if (timing.reported) return;
    timing.reported = true;
    const ms = Math.round(performance.now() - timing.startMs);
    const ctx = {
      metric: 'videoStart' as const,
      ms,
      src: shortenUrl(src),
    };
    if (ms >= VIDEO_START_ERROR_MS) {
      logger.error('[perf] video start very slow', ctx);
    } else if (ms >= VIDEO_START_WARN_MS) {
      logger.warn('[perf] video start slow', ctx);
    } else {
      logger.info('[perf] video start', ctx);
    }
    video.removeEventListener('playing', onPlaying);
    video.removeEventListener('error', onError);
  };
  const onError = () => {
    if (timing.reported) return;
    timing.reported = true;
    const ms = Math.round(performance.now() - timing.startMs);
    logger.error('[perf] video start error', {
      metric: 'videoStartError',
      ms,
      src: shortenUrl(src),
      err: String(video.error?.message ?? video.error?.code ?? 'unknown'),
    });
    video.removeEventListener('playing', onPlaying);
    video.removeEventListener('error', onError);
  };

  video.addEventListener('playing', onPlaying, { once: false });
  video.addEventListener('error', onError, { once: false });
}

// ── Media cache hit/miss ────────────────────────────────────────────

const CACHE_WINDOW_MS = 5 * 60 * 1000;
const cacheEvents: Array<{ ts: number; hit: boolean }> = [];

export function reportCacheLookup(hit: boolean): void {
  const now = Date.now();
  cacheEvents.push({ ts: now, hit });
  // Drop stale entries.
  while (cacheEvents.length > 0 && cacheEvents[0].ts < now - CACHE_WINDOW_MS) {
    cacheEvents.shift();
  }
}

export function getCacheHitRate(): { hits: number; total: number; rate: number } {
  const total = cacheEvents.length;
  const hits = cacheEvents.filter((e) => e.hit).length;
  return { hits, total, rate: total === 0 ? -1 : hits / total };
}

// ── Helpers ─────────────────────────────────────────────────────────

function shortenUrl(src: string): string {
  // Don't log full presigned R2 URLs (they're huge and they leak the
  // auth token). Just keep the path tail.
  try {
    const u = new URL(src);
    return `${u.host}${u.pathname.split('/').slice(-2).join('/')}`;
  } catch {
    return src.slice(0, 64);
  }
}
